import {
  Auth0AccountRole,
  FormFieldInfo,
  PublicFieldView,
  TaskStatus,
  VariableGroup,
} from "client/openapi";
import { PersonKind } from "client/openapi/models/PersonKind";
import "components/Dashboard/ManageTasksDialog/create.css";
import PeoplePicker from "components/Dashboard/ManageTasksDialog/PeoplePicker";
import FieldValueInput, { FieldValue } from "components/FieldValueInput";
import { Tick } from "components/Forms/SendFormControls";
import { COMPACT_INPUT, COMPACT_TEXTAREA } from "components/Inputs/compact";
import {
  TemplatePicker,
  versionsOf,
} from "components/MessageTemplates/SendTemplateControls";
import {
  Family,
  onlyKind,
  sendToFor,
} from "components/MessageTemplates/TriggerDialogs";
import {
  applyEdit,
  insertText,
  VariablePicker,
} from "components/MessageTemplates/WordingTools";
import { LABEL } from "components/ProfileDropdown/settingsStyles";
import { useId, useRef } from "react";
import { keyOfTaskPerson, taskPeopleOf } from "./ActivityTabParts";
import AssignToPicker from "./AssignToPicker";
import { closableTaskSteps, taskTitle } from "./AutomationsTabModel";
import CrmColumnField, { CrmColumnChoice } from "./CrmColumnField";
import { useReference } from "./hooks";
import { aboutRoles, actionsFor, isTaskKind, variablesFor } from "./kinds";
import { isSubjects, NOTE_LIMIT, toAdmins, withAction } from "./model";
import {
  ChoiceSelect,
  FieldRow,
  NumberBox,
  PillChoice,
  PillOption,
} from "./parts";
import {
  columnInfo,
  fieldByRef,
  ReferenceData,
  templateById,
} from "./reference";
import type {
  AssignTutorConfig,
  AssignTutorStep,
  CloseStatus,
  CloseTaskStep,
  CrmFieldConfig,
  CrmFieldStep,
  CrmMoveStep,
  CrmValue,
  DueUnit,
  MessageStep,
  NoteStep,
  PersonKey,
  PersonRole,
  Step,
  StepAction,
  SubjectsMode,
  TaskStep,
  Trigger,
  TutorPool,
  TutorShare,
} from "./types";
import { ACTION_LABELS } from "./words";

/**
 * The four step actions as pills. An action the trigger can't start (On
 * a schedule only sends messages) is shown off, with why on hover.
 */
export function StepActionPicker({
  value,
  onChange,
  trigger,
}: {
  value: StepAction;
  onChange: (action: StepAction) => void;
  trigger: Trigger;
}) {
  const ref = useReference();
  const allowed = actionsFor(ref, trigger);
  return (
    <PillChoice<StepAction>
      label="What this step does"
      value={value}
      size="sm"
      onChange={onChange}
      options={(Object.keys(ACTION_LABELS) as StepAction[]).map((action) => ({
        value: action,
        label: ACTION_LABELS[action],
        disabled: !allowed.includes(action),
        title: allowed.includes(action)
          ? undefined
          : "This trigger reaches everyone at once, so it can only send messages.",
      }))}
    />
  );
}

// ── Send a message ────────────────────────────────────────────────────────

/** Who gets a message: the people around its run, or the organization's admins. */
type WhoGets = Family | "admins";

/** The people around a run that Who gets it offers: those its trigger can reach. */
function aroundTheRun(
  only: Auth0AccountRole | undefined
): PillOption<WhoGets>[] {
  if (only === Auth0AccountRole.PARENT)
    return [{ value: "parents", label: "The parents" }];
  if (only === Auth0AccountRole.ORG_TUTOR)
    return [{ value: "none", label: "The tutors" }];
  return [
    { value: "students", label: "Student and parents" },
    { value: "parents", label: "Parents only" },
    { value: "none", label: "Tutors only" },
  ];
}

function MessageFields({
  step,
  onChange,
  trigger,
}: {
  step: MessageStep;
  onChange: (next: Step) => void;
  trigger: Trigger;
}) {
  const ref = useReference();
  const message = step.message;
  const template = templateById(ref, message.templateId);
  const versions = versionsOf(template ?? undefined);
  const only = onlyKind(trigger.kind, trigger.options || {}, ref.catalog);
  const family: Family = message.sendTo.includes("students")
    ? "students"
    : message.sendTo.includes("parents")
    ? "parents"
    : "none";
  const set = (patch: Partial<MessageStep["message"]>) =>
    onChange({ ...step, message: { ...message, ...patch } });

  return (
    <>
      <FieldRow label="Message template">
        <div className="w-full">
          <TemplatePicker
            templates={ref.templates}
            templateId={message.templateId}
            placeholder="Pick a template"
            onChange={(templateId) => {
              // A template goes by the ways it has, both when it has both.
              const picked = templateById(ref, templateId);
              set({
                templateId,
                ...(picked ? versionsOf(picked) : { sms: false, email: false }),
              });
            }}
          />
        </div>
      </FieldRow>
      <FieldRow label="Who gets it">
        <PillChoice<WhoGets>
          label="Who gets it"
          value={toAdmins(message.sendTo) ? "admins" : family}
          onChange={(next) =>
            set({
              sendTo:
                next === "admins"
                  ? ["admins"]
                  : sendToFor(only, next, next === "none"),
            })
          }
          options={[
            ...aroundTheRun(only),
            { value: "admins", label: "Admins only" },
          ]}
        />
      </FieldRow>
      <FieldRow label="Send by">
        {versions.sms && (
          <Tick checked={message.sms} onChange={(sms) => set({ sms })}>
            SMS
          </Tick>
        )}
        {versions.email && (
          <Tick checked={message.email} onChange={(email) => set({ email })}>
            Email
          </Tick>
        )}
        {template && !versions.sms && !versions.email && (
          <span className="text-red-600">
            Write the email or the text first.
          </span>
        )}
        {!template && (
          <span className="text-gray-500">Pick a template first.</span>
        )}
      </FieldRow>
    </>
  );
}

// ── Create a task ─────────────────────────────────────────────────────────

function TaskFields({
  step,
  onChange,
  trigger,
}: {
  step: TaskStep;
  onChange: (next: Step) => void;
  trigger: Trigger;
}) {
  const ref = useReference();
  const id = useId();
  const nameRef = useRef<HTMLInputElement>(null);
  const descriptionRef = useRef<HTMLTextAreaElement>(null);
  const task = step.task;
  const variables = variablesFor(ref, trigger);
  const set = (patch: Partial<TaskStep["task"]>) =>
    onChange({ ...step, task: { ...task, ...patch } });
  const aboutLabel = isTaskKind(trigger.kind)
    ? "Same as that task"
    : "The person it is about";

  return (
    <>
      <div className="mt-3">
        {/* Insert variable sits by the label, so its list opens under it
            inside the dialog. */}
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <label htmlFor={`${id}-name`} className={LABEL}>
            Task name
          </label>
          <VariablePicker
            groups={variables}
            onPick={(key) =>
              applyEdit(
                nameRef,
                insertText(nameRef.current, task.name, `{{${key}}}`),
                (name) => set({ name })
              )
            }
          />
        </div>
        <input
          id={`${id}-name`}
          ref={nameRef}
          type="text"
          className={`${COMPACT_INPUT} mt-1 w-full`}
          value={task.name}
          onChange={(e) => set({ name: e.target.value })}
        />
      </div>
      <div className="mt-3">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <label htmlFor={`${id}-description`} className={LABEL}>
            Description
          </label>
          <VariablePicker
            groups={variables}
            onPick={(key) =>
              applyEdit(
                descriptionRef,
                insertText(
                  descriptionRef.current,
                  task.description,
                  `{{${key}}}`
                ),
                (description) => set({ description })
              )
            }
          />
        </div>
        <textarea
          id={`${id}-description`}
          ref={descriptionRef}
          rows={2}
          className={`${COMPACT_TEXTAREA} mt-1 resize-y`}
          value={task.description}
          onChange={(e) => set({ description: e.target.value })}
        />
      </div>
      <div className="grid grid-cols-2 gap-x-3">
        <FieldRow label="Due">
          in
          <NumberBox
            label="Due in"
            value={task.due.n}
            min={0}
            max={365}
            onChange={(n) => set({ due: { ...task.due, n } })}
          />
          <ChoiceSelect<DueUnit>
            label="Days or weekdays"
            value={task.due.unit}
            options={[
              { value: "days", label: "day(s)" },
              { value: "weekdays", label: "weekday(s)" },
            ]}
            onChange={(unit) => set({ due: { ...task.due, unit } })}
            className="min-w-0 flex-1"
          />
        </FieldRow>
        <FieldRow label="About">
          <ChoiceSelect<"event" | "none">
            label="About"
            value={task.about}
            options={[
              { value: "event", label: aboutLabel },
              { value: "none", label: "Nobody" },
            ]}
            onChange={(about) => set({ about })}
            className="w-full"
          />
        </FieldRow>
      </div>
      <FieldRow label="Assign to">
        <AssignToPicker
          value={task.assignTo}
          onChange={(assignTo) => set({ assignTo })}
          trigger={trigger}
        />
      </FieldRow>
    </>
  );
}

// ── Move to a CRM column ──────────────────────────────────────────────────

/** A field as the app's value input takes it. */
function fieldView(info: FormFieldInfo): PublicFieldView {
  return {
    field: info.field,
    label: info.label,
    input: info.input,
    required: false,
    options: info.options ?? null,
    min_value: info.min_value ?? null,
    max_value: info.max_value ?? null,
  };
}

/** The values with one field's value set (or taken out when empty). */
function withValue(values: CrmValue[], field: string, value: FieldValue) {
  const rest = values.filter((v) => v.field !== field);
  const blank =
    value === null ||
    value === "" ||
    (Array.isArray(value) && value.length === 0);
  return blank ? rest : [...rest, { field, value }];
}

/**
 * The fields a move into a column needs (its rule doesn't set them on its
 * own), as the backend's choices say: none when it sets its own values.
 */
export function columnNeeds(ref: ReferenceData, key: string | null) {
  const info = columnInfo(ref, key);
  return info && !info.sets ? info.needs ?? [] : [];
}

function CrmMoveFields({
  step,
  onChange,
  trigger,
  fill,
}: {
  step: CrmMoveStep;
  onChange: (next: Step) => void;
  trigger: Trigger;
  fill: boolean;
}) {
  const ref = useReference();
  const roles = aboutRoles(ref, trigger);
  // A move can't put anyone in a built-in visualization's columns; the
  // others' are off, saying why, where a move can't put this person.
  const columns: CrmColumnChoice[] = ref.boards
    .filter((b) => !b.builtIn)
    .flatMap((b) =>
      b.columns.map((c) => ({
        key: c.key,
        boardId: b.id,
        boardName: b.name,
        name: c.name,
        refusal: !roles.includes(c.role)
          ? `It holds ${c.role}s, and this automation is not about ${c.role}s.`
          : columnInfo(ref, c.key)?.refusal ?? null,
      }))
    );
  const picked = `${step.crmMove.boardId}:${step.crmMove.columnId}`;
  const key = columns.some((c) => c.key === picked) ? picked : null;
  const needs = columnNeeds(ref, key);
  const values = step.crmMove.values ?? [];
  const set = (patch: Partial<CrmMoveStep["crmMove"]>) =>
    onChange({ ...step, crmMove: { ...step.crmMove, ...patch } });

  const row = fill ? "grid grid-cols-2 gap-x-3" : "flex flex-wrap gap-x-4";
  return (
    <>
      <CrmColumnField
        columns={columns}
        value={key}
        boardId={step.crmMove.boardId}
        onChange={(next, boardId) =>
          set({
            boardId,
            columnId: next === null ? null : Number(next.split(":")[1]),
            values: [],
          })
        }
        fill={fill}
      />
      {needs.length > 0 && (
        <div className={row}>
          {needs.map((info) => (
            <FieldRow key={info.field} label={info.label}>
              <div
                className={fill ? "w-full" : "min-w-[12rem] max-w-xs flex-1"}
              >
                <FieldValueInput
                  field={fieldView(info)}
                  value={
                    values.find((v) => v.field === info.field)?.value ?? null
                  }
                  onChange={(value) =>
                    set({ values: withValue(values, info.field, value) })
                  }
                />
              </div>
            </FieldRow>
          ))}
        </div>
      )}
    </>
  );
}

// ── Update a CRM field ────────────────────────────────────────────────────

const FIELD_GROUPS: { role: PersonRole; label: string }[] = [
  { role: "student", label: "Student fields" },
  { role: "parent", label: "Parent fields" },
  { role: "tutor", label: "Tutor fields" },
];

/** Students and tutors have subjects; the picker lists Subjects with each one's fields. */
const SUBJECT_ROLES: PersonRole[] = ["student", "tutor"];

/** The field picker's value for Subjects in a role's group (the step is the same either way). */
const subjectsChoice = (role: string) => `subjects|${role}`;

/** The org's subjects as the app's subject picker takes them. */
export function subjectsView(ref: ReferenceData): PublicFieldView {
  return {
    field: "subjects",
    label: "Subjects",
    input: "subjects",
    required: false,
    options: ref.subjects.map((s) => ({ id: s.id, name: s.name })),
    min_value: null,
    max_value: null,
  };
}

const MODES: { value: SubjectsMode; label: string }[] = [
  { value: "add", label: "Add" },
  { value: "remove", label: "Remove" },
];

function CrmFieldFields({
  step,
  onChange,
  trigger,
  fill,
}: {
  step: CrmFieldStep;
  onChange: (next: Step) => void;
  trigger: Trigger;
  fill: boolean;
}) {
  const ref = useReference();
  const roles = aboutRoles(ref, trigger);
  const config = step.crmField;
  const subjects = isSubjects(config) ? config : null;
  const field = subjects ? null : fieldByRef(ref, config.field);
  const set = (next: CrmFieldConfig) => onChange({ ...step, crmField: next });
  const subjectRoles = SUBJECT_ROLES.filter((role) => roles.includes(role));

  if (!ref.fields.length && !subjectRoles.length)
    return (
      <FieldRow label="CRM field">
        <p className="text-sm text-red-600">
          Add a CRM field first, on the CRM page.
        </p>
      </FieldRow>
    );
  const view: PublicFieldView | null = field
    ? {
        field: field.field,
        label: field.label,
        input: field.info.input,
        required: false,
        options: field.info.options ?? null,
        min_value: field.info.min_value ?? null,
        max_value: field.info.max_value ?? null,
      }
    : null;
  return (
    <div className={fill ? "" : "flex flex-wrap gap-x-4"}>
      <FieldRow label="Field">
        <ChoiceSelect
          label="CRM field"
          value={
            subjects
              ? subjectsChoice(subjectRoles[0] ?? "student")
              : config.field
          }
          options={FIELD_GROUPS.map(({ role, label }) => ({
            label,
            options: [
              ...(SUBJECT_ROLES.includes(role)
                ? [
                    {
                      value: subjectsChoice(role),
                      label: "Subjects",
                      isDisabled: !roles.includes(role),
                    },
                  ]
                : []),
              ...ref.fields
                .filter((f) => f.role === role)
                .map((f) => ({
                  value: f.field,
                  label: f.label,
                  isDisabled: !roles.includes(f.role),
                })),
            ],
          })).filter((g) => g.options.length > 0)}
          placeholder="Pick a field"
          searchable
          onChange={(next) => {
            if (next?.startsWith("subjects|")) {
              if (!subjects)
                set({ field: "subjects", mode: "add", subject_ids: [] });
            } else if (subjects || next !== config.field)
              set({ field: next, value: null });
          }}
          className={fill ? "w-full" : "min-w-[14rem] max-w-xs"}
        />
      </FieldRow>
      {subjects && (
        <FieldRow label="Add or remove">
          <PillChoice<SubjectsMode>
            label="Add or remove"
            value={subjects.mode}
            options={MODES}
            onChange={(mode) => set({ ...subjects, mode })}
          />
          <div
            className={
              fill ? "min-w-0 flex-1" : "min-w-[14rem] max-w-xs flex-1"
            }
          >
            <FieldValueInput
              field={subjectsView(ref)}
              value={subjects.subject_ids}
              onChange={(value) =>
                set({
                  ...subjects,
                  subject_ids: Array.isArray(value) ? value : [],
                })
              }
            />
          </div>
        </FieldRow>
      )}
      {!subjects && view && (
        <FieldRow label="Set it to">
          <div className={fill ? "w-full" : "min-w-[14rem] max-w-xs flex-1"}>
            <FieldValueInput
              field={view}
              value={isSubjects(config) ? null : config.value}
              onChange={(value) => set({ field: config.field, value })}
            />
          </div>
        </FieldRow>
      )}
    </div>
  );
}

// ── Close a task ──────────────────────────────────────────────────────────

const CLOSE_STATUSES: PillOption<CloseStatus>[] = [
  { value: TaskStatus.COMPLETE, label: "Complete" },
  { value: TaskStatus.CANCELLED, label: "Cancelled" },
];

/** Mark as: Complete or Cancelled (a Close a task step, and its Change it first). */
export function MarkAsField({
  value,
  onChange,
}: {
  value: CloseStatus;
  onChange: (status: CloseStatus) => void;
}) {
  return (
    <FieldRow label="Mark as">
      <PillChoice<CloseStatus>
        label="Mark as"
        value={value}
        options={CLOSE_STATUSES}
        onChange={onChange}
      />
    </FieldRow>
  );
}

/** Which earlier task step made the task (the one right before first), and Mark as. */
function CloseTaskFields({
  step,
  onChange,
  steps,
  fill,
}: {
  step: CloseTaskStep;
  onChange: (next: Step) => void;
  steps: Step[];
  fill: boolean;
}) {
  const ref = useReference();
  const tasks = closableTaskSteps(steps, step.id);
  const set = (patch: Partial<CloseTaskStep["closeTask"]>) =>
    onChange({ ...step, closeTask: { ...step.closeTask, ...patch } });
  return (
    <>
      <FieldRow label="Task">
        {tasks.length ? (
          <ChoiceSelect
            label="Task"
            value={step.closeTask.stepId}
            options={tasks.map((s) => ({
              value: s.id,
              label: taskTitle(ref, s.task.name),
            }))}
            placeholder="Pick a task"
            onChange={(stepId) => set({ stepId })}
            className={fill ? "w-full" : "min-w-[14rem] max-w-xs"}
          />
        ) : (
          <p className="text-sm text-red-600">
            Add a Create a task step before this one first.
          </p>
        )}
      </FieldRow>
      <MarkAsField
        value={step.closeTask.status}
        onChange={(status) => set({ status })}
      />
    </>
  );
}

// ── Assign a tutor ────────────────────────────────────────────────────────

const TUTOR_POOLS: PillOption<TutorPool>[] = [
  { value: "all", label: "All tutors" },
  { value: "subject", label: "Tutors who teach" },
  { value: "specific", label: "Specific tutors" },
];

const TUTOR_SHARES: PillOption<TutorShare>[] = [
  { value: "turns", label: "Take turns" },
  { value: "all", label: "All of them" },
];

/** The organization's tutors, as the task form's people picker (Specific tutors, and Change it first). */
export function TutorsField({
  value,
  onChange,
  label,
  showLabel = false,
}: {
  value: PersonKey[];
  onChange: (tutors: PersonKey[]) => void;
  label: string;
  /** Its label over it (Change it first); else only for screen readers (Tutors' pills label it). */
  showLabel?: boolean;
}) {
  const ref = useReference();
  const id = useId();
  return (
    <div className="task-form">
      <label
        htmlFor={`${id}-tutors`}
        className={showLabel ? `${LABEL} mb-1 block` : "sr-only"}
      >
        {label}
      </label>
      <PeoplePicker
        inputId={`${id}-tutors`}
        people={taskPeopleOf(ref, ref.tutors)}
        roles={[PersonKind.TUTOR]}
        value={taskPeopleOf(ref, value)}
        onChange={(next) => onChange(next.map(keyOfTaskPerson))}
        placeholder="Find a tutor"
      />
    </div>
  );
}

/** Who it chooses from (all tutors, tutors who teach a subject, or specific tutors), then Take turns or All of them. */
function AssignTutorFields({
  step,
  onChange,
}: {
  step: AssignTutorStep;
  onChange: (next: Step) => void;
}) {
  const ref = useReference();
  const config = step.assignTutor;
  const set = (patch: Partial<AssignTutorConfig>) =>
    onChange({ ...step, assignTutor: { ...config, ...patch } });
  return (
    <>
      <FieldRow label="Tutors">
        <PillChoice<TutorPool>
          label="Tutors"
          value={config.choose}
          options={TUTOR_POOLS}
          onChange={(choose) => set({ choose })}
        />
        {config.choose === "subject" && (
          <ChoiceSelect
            label="Subject"
            value={config.subject}
            options={ref.subjects.map((s) => ({ value: s.id, label: s.name }))}
            placeholder="Pick a subject"
            onChange={(subject) => set({ subject })}
            className="min-w-[10rem] flex-1"
          />
        )}
      </FieldRow>
      {config.choose === "specific" && (
        <div className="mt-2">
          <TutorsField
            label="Specific tutors"
            value={config.tutors}
            onChange={(tutors) => set({ tutors })}
          />
        </div>
      )}
      <FieldRow label="Assign">
        <PillChoice<TutorShare>
          label="Assign"
          value={config.share}
          options={TUTOR_SHARES}
          onChange={(share) => set({ share })}
        />
      </FieldRow>
    </>
  );
}

// ── Add a note ────────────────────────────────────────────────────────────

/** A note's text: with Insert variable by its label (variables), as Task name has it. */
export function NoteTextField({
  value,
  onChange,
  variables,
}: {
  value: string;
  onChange: (text: string) => void;
  /** Insert variable's groups; none: the text as written (Change it first). */
  variables?: VariableGroup[];
}) {
  const id = useId();
  const textRef = useRef<HTMLTextAreaElement>(null);
  return (
    <div className="mt-3">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <label htmlFor={`${id}-note`} className={LABEL}>
          Note
        </label>
        {variables && (
          <VariablePicker
            groups={variables}
            onPick={(key) =>
              applyEdit(
                textRef,
                insertText(textRef.current, value, `{{${key}}}`),
                onChange
              )
            }
          />
        )}
      </div>
      <textarea
        id={`${id}-note`}
        ref={textRef}
        rows={4}
        aria-invalid={value.length > NOTE_LIMIT || undefined}
        className={`${COMPACT_TEXTAREA} mt-1 resize-y`}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

function NoteFields({
  step,
  onChange,
  trigger,
}: {
  step: NoteStep;
  onChange: (next: Step) => void;
  trigger: Trigger;
}) {
  const ref = useReference();
  return (
    <NoteTextField
      value={step.note.text}
      onChange={(text) => onChange({ ...step, note: { ...step.note, text } })}
      variables={variablesFor(ref, trigger)}
    />
  );
}

/**
 * The fields of a step's action: Send a message (template, who gets it,
 * send by), Create a task (name and description, each with Insert variable,
 * due, about, Assign to), Move to a CRM column (visualization and
 * column), Update a CRM field (field and value), Close a task (which
 * earlier task step's task, and Mark as), Assign a tutor (which tutors,
 * and how), or Add a note (its text with Insert variable). Pass the
 * automation's trigger: it decides who the step can reach, and which
 * choices apply; and its steps, for Close a task's earlier task steps.
 * fill (the step dialog): every field spans the dialog's width, and
 * Visualization and Column share a row, so their edges line up.
 */
export default function StepActionFields({
  step,
  onChange,
  trigger,
  fill = false,
  steps = [step],
}: {
  step: Step;
  onChange: (next: Step) => void;
  trigger: Trigger;
  fill?: boolean;
  /** The automation's steps, this one among them (Close a task picks an earlier task step). */
  steps?: Step[];
}) {
  switch (step.action) {
    case "message":
      return (
        <MessageFields step={step} onChange={onChange} trigger={trigger} />
      );
    case "task":
      return <TaskFields step={step} onChange={onChange} trigger={trigger} />;
    case "crm_move":
      return (
        <CrmMoveFields
          step={step}
          onChange={onChange}
          trigger={trigger}
          fill={fill}
        />
      );
    case "crm_field":
      return (
        <CrmFieldFields
          step={step}
          onChange={onChange}
          trigger={trigger}
          fill={fill}
        />
      );
    case "close_task":
      return (
        <CloseTaskFields
          step={step}
          onChange={onChange}
          steps={steps}
          fill={fill}
        />
      );
    case "assign_tutor":
      return <AssignTutorFields step={step} onChange={onChange} />;
    case "add_note":
      return <NoteFields step={step} onChange={onChange} trigger={trigger} />;
  }
}

/** Switches a step to another action, keeping its id, position and Ask first. */
export function useSwitchAction(trigger: Trigger) {
  const ref = useReference();
  return (step: Step, action: StepAction) =>
    withAction(ref, trigger, step, action);
}
